import React, { useState, useEffect } from "react";
import { getHome } from "../../services/adminService";
import {
  showSpinner,
  closeSpinner,
  modal,
  alert,
} from "../../js/modules/Dialogs";
import { sessionExpired } from "../../config/messages";
import { toScreen } from "../../js/modules/Routing";
import withNavigate from "./withNavigate";
import PropTypes from "prop-types";
import { getMessagesFilterCounts } from "../../services/messageService";
import TopNav from "../commonUI/TopHeader/TopHeader";

const Home = ({ userDetails, navigate, updateState }) => {
  const [jobs, setJobs] = useState({ total: "", quoted: "", quoteless: "" });
  const [filterCount, setFilterCount] = useState({
    unread: null,
    disputes: null,
    flagged: null,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [clients, setClients] = useState({
    total: "",
    active: "",
    blocked: "",
  });
  const [mavs, setMavs] = useState({ total: "", pending: "", blocked: "" });
  const [messages, setMessages] = useState({
    total: 0,
    readStandard: 0,
    readDisputes: 0,
    unreadStandard: 0,
    unreadDisputes: 0,
  });
  const [lastLogin, setLastLogin] = useState("");

  useEffect(() => {
    handleFetchFilterCounts();
  }, []);

  const handleFetchFilterCounts = async () => {
    showSpinner();
    try {
      const queryParams = {
        page: page,
        limit: pageSize,
      };
      const resJson = await getMessagesFilterCounts(queryParams);
      closeSpinner();
      setFilterCount({
        unread: resJson?.unread?.count,
        disputes: resJson?.disputes?.count,
        flagged: resJson?.flagged?.count,
      });
    } catch (error) {
      closeSpinner();
      modal(error.message || "Something went wrong!");
    }
  };

  const fetchHomeData = async (event) => {
    if (event) event.preventDefault();
    showSpinner();

    const theUser = {
      accountInfo: {
        username: userDetails.accountInfo?.username,
        email: userDetails.accountInfo?.email,
      },
    };

    try {
      const resJson = await getHome(theUser);
      closeSpinner();
      setJobs(resJson?.jobs);
      setClients(resJson?.clients);
      setMavs(resJson?.mavs);
      setMessages(resJson?.messages);
      setLastLogin(resJson?.lastLogin);
    } catch (error) {
      closeSpinner();
      if (error.response?.status === 401) {
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
      } else {
        alert(error.message || "Failed to fetch home data");
      }
    }
  };

  useEffect(() => {
    fetchHomeData();
    return () => {
      const updatedUserDetails = {
        ...userDetails,
        messages: messages,
      };
      updateState("userDetails", updatedUserDetails);
    };
  }, []);

  const handleViewMessages = (e, filterType) => {
    const updatedHistoryState = {
      ...(window.history.state.usr?.selectedFilter || {}),
      usr: {
        ...window.history.state.usr?.selectedFilter.usr,
        selectedFilter: filterType, // Change this to the new filter value
      },
    };
    toScreen(e, navigate, "/messages");
    window.history.replaceState(updatedHistoryState, "");
  };

  return (
    <>
      <TopNav title={"Home"} />
      <div className="global-body">
        <div className="global-body-container">
          <div className="metric-card-wrapper">
            <div
              className="metric-card"
              onClick={(e) => toScreen(e, navigate, "/jobs")}
            >
              <div className="metric-card-title">Open Jobs:</div>
              <h2 className="metric-card-number">{jobs?.total}</h2>
              <ul className="metric-card-list">
                <li className="metric-card-item">
                  <span className="metric-label">Quoted:</span>
                  <span className="metric-label">{jobs?.quoted}</span>
                </li>
                <li className="metric-card-item">
                  <span className="metric-label">Not quoted:</span>
                  <span className="metric-label">{jobs?.quoteless}</span>
                </li>
              </ul>
            </div>
            <div
              className="metric-card"
              onClick={(e) => toScreen(e, navigate, "/clients")}
            >
              <div className="metric-card-title">Clients:</div>
              <h2 className="metric-card-number active">{clients?.total}</h2>
              <ul className="metric-card-list">
                <li className="metric-card-item">
                  <span className="metric-label active">Active:</span>
                  <span className="metric-label active">{clients?.active}</span>
                </li>
                <li className="metric-card-item">
                  <span className="metric-label">Blocked:</span>
                  <span className="metric-label">{clients?.blocked}</span>
                </li>
              </ul>
            </div>
            <div
              className="metric-card"
              onClick={(e) => toScreen(e, navigate, "/mavs")}
            >
              <div className="metric-card-title">MAVs:</div>
              <h2 className="metric-card-number active">{mavs?.total}</h2>
              <ul className="metric-card-list">
                <li className="metric-card-item">
                  <span className="metric-label">Pending:</span>
                  <span className="metric-label">{mavs?.pending}</span>
                </li>
                <li className="metric-card-item">
                  <span className="metric-label">Blocked:</span>
                  <span className="metric-label">{mavs?.blocked}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="task-wrapper">
            <h2 className="text-xl mb-16">Tasks</h2>
            <ul className="task-list">
              <li className="task-list-item">
                <span className="text-gray">
                  Unread Messages:{" "}
                  <span className="ms-2 text-primary">
                    {`${filterCount?.unread ?? 0}`}
                  </span>
                </span>
                <button
                  type="button"
                  className="view-btn"
                  onClick={(e) => handleViewMessages(e, "unread")}
                >
                  View
                </button>
              </li>
              <li className="task-list-item">
                <span className="text-gray">
                  Disputes:{" "}
                  <span className="ms-2 text-primary">
                    {`${filterCount?.disputes ?? 0}`}
                  </span>
                </span>
                <button
                  type="button"
                  className="view-btn"
                  onClick={(e) => handleViewMessages(e, "disputes")}
                >
                  View
                </button>
              </li>
            </ul>
          </div>

          <div className="d-flex justify-content-end align-items-center">
            <p className="text-gray text-sm">
              Last login: <span className="text-primary">{lastLogin}</span>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

Home.propTypes = {
  userDetails: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  updateState: PropTypes.func.isRequired,
};

export default withNavigate(Home);
